import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Send, CheckCircle } from "lucide-react";
import { sanitizeInput, isValidEmail, isValidPhone, appointmentRateLimiter } from "@/lib/security";

interface QuoteRequestModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface QuoteFormData {
  fullName: string;
  email: string;
  phone: string;
  companyName: string;
  website: string;
  currentSituation: string;
  services: string[];
  budget: string;
  timeline: string;
  comments: string;
}

export function QuoteRequestModal({ open, onOpenChange }: QuoteRequestModalProps) {
  const [formData, setFormData] = useState<QuoteFormData>({
    fullName: "",
    email: "",
    phone: "",
    companyName: "",
    website: "",
    currentSituation: "",
    services: [],
    budget: "",
    timeline: "",
    comments: "",
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const serviceOptions = [
    { id: "seo", label: "SEO" },
    { id: "social", label: "Redes Sociales" },
    { id: "branding", label: "Branding" },
    { id: "advertising", label: "Publicidad" },
    { id: "web", label: "Desarrollo Web" },
    { id: "other", label: "Otro" },
  ];

  const budgetOptions = [
    { value: "less-1000", label: "Menos de 1.000€" },
    { value: "1000-3000", label: "1.000€ - 3.000€" },
    { value: "3000-5000", label: "3.000€ - 5.000€" },
    { value: "more-5000", label: "Más de 5.000€" },
  ];

  const handleServiceChange = (serviceId: string, checked: boolean) => {
    setFormData(prev => ({
      ...prev,
      services: checked
        ? [...prev.services, serviceId]
        : prev.services.filter(s => s !== serviceId)
    }));
  };

  const resetForm = () => {
    setFormData({
      fullName: "",
      email: "",
      phone: "",
      companyName: "",
      website: "",
      currentSituation: "",
      services: [],
      budget: "",
      timeline: "",
      comments: "",
    });
    setIsSubmitted(false);
  };

  const handleClose = () => {
    resetForm();
    onOpenChange(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Rate limiting check
    if (!appointmentRateLimiter.isAllowed(formData.email || "anonymous")) {
      toast({
        title: "Demasiadas solicitudes",
        description: "Has enviado muchas solicitudes recientemente. Por favor espera unos minutos.",
        variant: "destructive",
      });
      return;
    }
    
    // Validate required fields
    if (!formData.fullName.trim() || !formData.email.trim()) {
      toast({
        title: "Error",
        description: "Por favor, completa los campos obligatorios.",
        variant: "destructive",
      });
      return;
    }

    // Validate email format
    if (!isValidEmail(formData.email)) {
      toast({
        title: "Error de validación",
        description: "Por favor, introduce un email válido.",
        variant: "destructive",
      });
      return;
    }

    // Validate phone if provided
    if (formData.phone && !isValidPhone(formData.phone)) {
      toast({
        title: "Error de validación",
        description: "Por favor, introduce un número de teléfono válido.",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    
    try {
      // Sanitize form data before sending
      const sanitizedData = {
        fullName: sanitizeInput(formData.fullName),
        email: sanitizeInput(formData.email),
        phone: formData.phone ? sanitizeInput(formData.phone) : "",
        companyName: sanitizeInput(formData.companyName),
        website: sanitizeInput(formData.website),
        currentSituation: sanitizeInput(formData.currentSituation),
        services: formData.services.map(serviceId => 
          serviceOptions.find(opt => opt.id === serviceId)?.label || serviceId
        ).join(", "),
        budget: sanitizeInput(formData.budget),
        timeline: sanitizeInput(formData.timeline),
        comments: sanitizeInput(formData.comments),
      };

      const { error } = await supabase.functions.invoke('send-quote-request', {
        body: sanitizedData
      });

      if (error) throw error;

      setIsSubmitted(true);
      toast({
        title: "¡Solicitud enviada!",
        description: "Nuestro equipo se pondrá en contacto contigo en menos de 24 horas.",
      });
      
    } catch (error) {
      console.error('Error sending quote request:', error);
      toast({
        title: "Error",
        description: "Ha ocurrido un error al enviar tu solicitud. Por favor, inténtalo de nuevo.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-card border-border">
        <DialogHeader>
          <DialogTitle className="text-2xl text-foreground">
            {isSubmitted ? "¡Solicitud Enviada!" : "Solicitar Presupuesto Personalizado"}
          </DialogTitle>
        </DialogHeader>

        {isSubmitted ? (
          <div className="text-center py-8">
            <CheckCircle className="h-16 w-16 text-primary mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-foreground mb-2">
              ¡Gracias por tu solicitud!
            </h3>
            <p className="text-muted-foreground mb-6">
              Nuestro equipo se pondrá en contacto contigo en menos de 24 horas.
            </p>
            <div className="space-y-3">
              <Button asChild className="w-full">
                <a href="/reservar-cita">Agendar Consultoría</a>
              </Button>
              <Button variant="outline" onClick={handleClose} className="w-full">
                Cerrar
              </Button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Información Personal */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="modal-fullName" className="text-foreground">
                  Nombre completo *
                </Label>
                <Input
                  id="modal-fullName"
                  value={formData.fullName}
                  onChange={(e) => setFormData(prev => ({ ...prev, fullName: e.target.value }))}
                  placeholder="Tu nombre completo"
                  required
                  className="bg-input border-border text-foreground"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="modal-email" className="text-foreground">
                  Email de contacto *
                </Label>
                <Input
                  id="modal-email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="tu@email.com"
                  required
                  className="bg-input border-border text-foreground"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="modal-phone" className="text-foreground">
                  Teléfono
                </Label>
                <Input
                  id="modal-phone"
                  value={formData.phone}
                  onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                  placeholder="+34 XXX XXX XXX"
                  className="bg-input border-border text-foreground"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="modal-companyName" className="text-foreground">
                  Nombre de la empresa
                </Label>
                <Input
                  id="modal-companyName"
                  value={formData.companyName}
                  onChange={(e) => setFormData(prev => ({ ...prev, companyName: e.target.value }))}
                  placeholder="Nombre de tu empresa"
                  className="bg-input border-border text-foreground"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="modal-website" className="text-foreground">
                Página web de la empresa (si tiene)
              </Label>
              <Input
                id="modal-website"
                value={formData.website}
                onChange={(e) => setFormData(prev => ({ ...prev, website: e.target.value }))}
                placeholder="https://tuempresa.com"
                className="bg-input border-border text-foreground"
              />
            </div>

            {/* Situación Actual - Reduced size for modal */}
            <div className="space-y-2">
              <Label htmlFor="modal-currentSituation" className="text-foreground">
                Estado actual de la empresa / marketing
              </Label>
              <Textarea
                id="modal-currentSituation"
                value={formData.currentSituation}
                onChange={(e) => setFormData(prev => ({ ...prev, currentSituation: e.target.value }))}
                placeholder="Describe brevemente tu situación actual..."
                className="bg-input border-border text-foreground min-h-[80px]"
              />
            </div>

            {/* Servicios */}
            <div className="space-y-3">
              <Label className="text-foreground">
                Servicios que necesitas
              </Label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {serviceOptions.map((service) => (
                  <div key={service.id} className="flex items-center space-x-2">
                    <Checkbox
                      id={`modal-${service.id}`}
                      checked={formData.services.includes(service.id)}
                      onCheckedChange={(checked) => 
                        handleServiceChange(service.id, checked as boolean)
                      }
                    />
                    <Label htmlFor={`modal-${service.id}`} className="text-foreground text-sm">
                      {service.label}
                    </Label>
                  </div>
                ))}
              </div>
            </div>

            {/* Presupuesto y Plazo */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="modal-budget" className="text-foreground">
                  Presupuesto estimado
                </Label>
                <Select 
                  value={formData.budget} 
                  onValueChange={(value) => setFormData(prev => ({ ...prev, budget: value }))}
                >
                  <SelectTrigger className="bg-input border-border text-foreground">
                    <SelectValue placeholder="Selecciona un rango" />
                  </SelectTrigger>
                  <SelectContent className="bg-popover border-border">
                    {budgetOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="modal-timeline" className="text-foreground">
                  Plazo esperado
                </Label>
                <Input
                  id="modal-timeline"
                  value={formData.timeline}
                  onChange={(e) => setFormData(prev => ({ ...prev, timeline: e.target.value }))}
                  placeholder="Ej: 3 meses, ASAP"
                  className="bg-input border-border text-foreground"
                />
              </div>
            </div>

            {/* Comentarios - Reduced for modal */}
            <div className="space-y-2">
              <Label htmlFor="modal-comments" className="text-foreground">
                Comentarios adicionales
              </Label>
              <Textarea
                id="modal-comments"
                value={formData.comments}
                onChange={(e) => setFormData(prev => ({ ...prev, comments: e.target.value }))}
                placeholder="Información adicional..."
                className="bg-input border-border text-foreground min-h-[60px]"
              />
            </div>

            {/* Submit Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 pt-4">
              <Button 
                type="submit" 
                disabled={isSubmitting}
                className="flex-1 bg-primary hover:bg-primary/90 text-primary-foreground"
              >
                {isSubmitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2"></div>
                    Enviando...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Enviar Solicitud
                  </>
                )}
              </Button>
              
              <Button 
                type="button" 
                variant="outline" 
                onClick={handleClose}
                className="flex-1"
              >
                Cancelar
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}